import { format } from 'date-fns';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import AppContext from '../../../components/App/AppContext';
import FooterCta from '../../../components/FooterCta';
import Form from '../../../components/Form';
import FormField from '../../../components/FormField';
import SlidePillToggle from '../../../components/FormFieldPill';
import FormFieldSelect from '../../../components/FormFieldSelect';

import DetailsFormPhoneField from './DetailsFormPhoneField';
import DetailsFormSchema from './DetailsFormSchema';
import DetailsFormSchemaTakeaway from './DetailsFormSchemaTakeaway';
import DetailsFormSchemaVouchers from './DetailsFormSchemaVouchers';
import MarketingOptin from './MarketingOptin';

import formatCurrency from '../../../utils/formatCurrency';
import getDetailsFormFromLocalStorage from '../../../utils/getDetailsFormFromLocalStorage';
import countryCodes from './countryCodes';

import cySelectors from '../../../tests/cySelectors';

import { IVAN_RAMEN_VENUE_ID } from '../../../utils/constants';

const removeTakeawayOption = (array = []) => {
  if (!array) return array;

  return array.filter(({ tableName }) => tableName !== 'Takeaway');
};

const DetailsForm = ({ handlePatchDetails, isVouchers = false }) => {
  const [patchDetailsError, setPatchDetailsError] = useState(false);
  const [pickupTimesOptionsTimeSlots, setPickupTimesOptionsTimeSlots] = useState(null);

  const [formInstance, setFormInstance] = useState();
  const { data: venueData } = useSelector((state) => state.venue);
  const { data: userData } = useSelector((state) => state.user);
  const { allowMarketing } = userData || {};
  const { isMarketingEnabled, setIsMarketingEnabled } = useContext(AppContext);
  const { data: orderData, loading: orderLoading } = useSelector((state) => state.order);
  const { data: pickupTimesData } = useSelector((state) => state.pickupTimes);
  const { data: tablesData, loading: tablesLoading } = useSelector((state) => state.tables);
  const { total, isUserDetailsEditable } = orderData || {};
  const { isTakeawayEnabled, isOpen, venueId } = venueData || {};
  const isTakeAwayOnly = isTakeawayEnabled && (!isOpen || venueId === IVAN_RAMEN_VENUE_ID);
  const [orderType, setOrderType] = useState(isTakeAwayOnly ? 'Collection' : 'Dine-In');

  const sortedTables =
    tablesData && isTakeawayEnabled ? removeTakeawayOption(tablesData?.slice()) : tablesData;

  const tablesOption = sortedTables?.map((table) => ({
    value: table.tableName,
    label: table.tableName,
  }));

  const formattedTotal = formatCurrency(total);
  const { email, name, phone, countryCode, tableName } = getDetailsFormFromLocalStorage();

  const FinalDetailsFormSchema = () => {
    if (isVouchers) {
      return DetailsFormSchemaVouchers;
    }
    if (orderType === 'Dine-In') {
      return DetailsFormSchema;
    }
    return DetailsFormSchemaTakeaway;
  };

  const finalCountryCode = useMemo(
    () => countryCode || { label: '+44 United Kingdom', value: '+44' },
    [countryCode],
  );
  const validTableName = tablesOption?.find(
    (table) => table.value.toLowerCase() === tableName?.value.toLowerCase(),
  );

  const takeawayTableName = useMemo(
    () => (isTakeAwayOnly ? { value: 'Takeaway', label: 'Takeaway' } : validTableName || null),
    [isTakeAwayOnly, validTableName],
  );
  const finalTableName = tablesOption?.length === 1 ? tablesOption[0] : takeawayTableName;

  const finalPickupTime = useMemo(
    () => (pickupTimesOptionsTimeSlots ? pickupTimesOptionsTimeSlots[0] : null),
    [pickupTimesOptionsTimeSlots],
  );
  const setDefaultPickupTimes = useCallback(() => {
    const { pickupTimes } = pickupTimesData || {};
    const defaultPickupTimes = pickupTimes[0].pickupTimes.map((time) => ({
      value: time,
      label: time,
    }));
    setPickupTimesOptionsTimeSlots(defaultPickupTimes);
  }, [pickupTimesData, setPickupTimesOptionsTimeSlots]);

  const { isValid, reset, getValues } = formInstance || {};

  const defaultValues = useMemo(
    () => ({
      tableName: finalTableName,
      name,
      email,
      countryCode: finalCountryCode,
      phone,
      pickupTime: finalPickupTime,
    }),
    [email, finalCountryCode, finalPickupTime, finalTableName, name, phone],
  );

  const vouchersDefaultValues = {
    name,
    email,
    countryCode: finalCountryCode,
    phone,
  };

  const finalDefaultValues = isVouchers ? vouchersDefaultValues : defaultValues;

  const PickupTimeSelector = (
    <div className="checkout__form__takeaway">
      <FormFieldSelect
        name="pickupTime"
        options={pickupTimesOptionsTimeSlots}
        label="Collection time"
        data-cy={cySelectors.CHECKOUT_TAKEAWAY_TIME}
      />
    </div>
  );

  const canChooseOrderType = isTakeawayEnabled && isUserDetailsEditable;
  const showTableSelector = !canChooseOrderType || orderType === 'Dine-In';

  useEffect(() => {
    const {
      countryCode: countryCodeFromForm,
      tableName: tableNameFromForm,
      pickupTime: pickupTimeFromForm,
    } = getValues ? getValues() || {} : {};
    if (reset && !isVouchers) {
      reset(
        {
          tableName: tableNameFromForm || finalTableName,
          countryCode: countryCodeFromForm || finalCountryCode,
          pickupTime: pickupTimeFromForm || finalPickupTime,
        },
        { keepErrors: true, keepDirty: true },
      );
    }
  }, [
    finalDefaultValues,
    finalTableName,
    isVouchers,
    reset,
    getValues,
    finalCountryCode,
    finalPickupTime,
  ]);

  useEffect(() => {
    if (orderType === 'Collection' && pickupTimesData) {
      setDefaultPickupTimes();
    }
  }, [orderType, pickupTimesData, setDefaultPickupTimes]);

  const handleOnSubmit = (values) => {
    const { setError } = formInstance;
    const finalValues =
      orderType === 'Collection'
        ? {
            ...values,
            tableName: { value: 'Takeaway', label: 'Takeaway' },
            pickupDay: {
              value: format(new Date(), 'yyyy-MM-dd'),
              label: format(new Date(), 'EEEE'),
            },
          }
        : (({ pickupTime, pickupDay, ...rest }) => rest)(values);

    handlePatchDetails(finalValues, setPatchDetailsError, setError);
  };

  return (
    <div className="details__form" data-cy={cySelectors.DETAILS_FORM}>
      <Form
        handleOnSubmit={handleOnSubmit}
        validationSchema={FinalDetailsFormSchema()}
        setFormInstance={setFormInstance}
        validateOnLoad
        defaultValues={finalDefaultValues}
        mode="onChange"
      >
        <>
          {!isVouchers && (
            <>
              {isTakeAwayOnly ? (
                PickupTimeSelector
              ) : (
                <>
                  {canChooseOrderType && (
                    <SlidePillToggle
                      label="Please select whether you'll be dining in or collecting your order"
                      name="orderType"
                      options={['Dine-In', 'Collection']}
                      value={orderType}
                      onChange={setOrderType}
                    />
                  )}

                  {showTableSelector && (
                    <FormFieldSelect
                      name="tableName"
                      options={tablesOption}
                      placeholder="Your table number"
                      helpText="So we know where to bring your order"
                      label="Your table number"
                      readOnly={isTakeAwayOnly}
                      data-cy={cySelectors.DETAILS_FORM_TABLE}
                    />
                  )}

                  {orderType === 'Collection' && PickupTimeSelector}
                </>
              )}
            </>
          )}
        </>
        <FormField
          name="name"
          type="text"
          placeholder="Your name"
          label="Your name"
          helpText="We'll use this to find you at your table"
          autocomplete="name"
          data-cy={cySelectors.DETAILS_FORM_NAME}
        />
        <FormField
          name="email"
          type="text"
          placeholder="Your email"
          label="Your email"
          helpText="We'll use this to send you order updates"
          autocomplete="email"
          data-cy={cySelectors.DETAILS_FORM_EMAIL}
        />
        <DetailsFormPhoneField countryCodes={countryCodes} />

        {!isVouchers && (
          <MarketingOptin
            isUserOptedIn={allowMarketing}
            isMarketingEnabled={isMarketingEnabled}
            setIsMarketingEnabled={setIsMarketingEnabled}
          />
        )}
        <FooterCta
          type="submit"
          disabled={!isValid}
          loading={orderLoading || tablesLoading}
          data-cy={cySelectors.SUBMIT_BTN}
        >
          <span>{patchDetailsError ? 'Please try again' : 'Continue'}</span>
          <span>{formattedTotal}</span>
        </FooterCta>
      </Form>
    </div>
  );
};

export default DetailsForm;
